import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import {
    Box,
    Heading,
    Input,
    VStack,
    Select,
    Flex,
    useBreakpointValue,
    Button
} from '@chakra-ui/react';
import { useEvents } from '../hooks/useEvents';
import { useTimezone } from '../contexts/TimezoneContext';
import { groupEventsByDate } from '../utils/eventUtils';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { EmptyState } from '../components/common/EmptyState';
import { TradingEventsTable } from '../components/events/TradingEventsTable';
import { DexchPasswordProtection } from '../components/DexchPasswordProtection';

const DexchTradingContent: React.FC = () => {
    const [selectedDate, setSelectedDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
    const [selectedRuleSet, setSelectedRuleSet] = useState<string>('');
    const [timeZone] = useTimezone();

    // Use our custom hook to manage events data (without cancel functionality)
    const { events, isLoading } = useEvents(selectedDate);

    // Filter events to only show competitions where name starts with "DIM Cricket"
    const filteredEvents = useMemo(() => {
        return events.filter(event => event.competition?.name?.startsWith('DIM Cricket'));
    }, [events]);

    // Group events by date (in the selected timezone) and filter by rule set
    const groupedEvents = groupEventsByDate(filteredEvents, selectedRuleSet, timeZone);

    // Use responsive values for UI elements
    const buttonSize = useBreakpointValue({ base: 'xs', md: 'sm' });
    const headingSize = useBreakpointValue({ base: 'sm', md: 'md' });

    return (
        <Box p={{ base: 2, md: 4 }}>
            <VStack spacing={{ base: 3, md: 4 }} align="stretch">
                <Flex
                    direction={{ base: 'column', sm: 'row' }}
                    justify="space-between"
                    align={{ base: 'flex-start', sm: 'center' }}
                    gap={{ base: 2, sm: 0 }}
                >
                    <Heading size={headingSize}>Dexch Trading Dashboard</Heading>
                </Flex>

                <Box mb={{ base: 3, md: 4 }}>
                    <Flex
                        direction={{ base: 'column', md: 'row' }}
                        gap={{ base: 2, md: 4 }}
                        align={{ base: 'stretch', md: 'center' }}
                    >
                        <Input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            size={buttonSize}
                            maxWidth={{ base: '100%', md: '200px' }}
                            width={{ base: '100%', md: 'auto' }}
                        />
                        <Select
                            value={selectedRuleSet}
                            onChange={(e) => setSelectedRuleSet(e.target.value)}
                            size={buttonSize}
                            maxWidth={{ base: '100%', md: '150px' }}
                            width={{ base: '100%', md: 'auto' }}
                            placeholder="All RuleSets"
                        >
                            <option value="T1">T1</option>
                            <option value="T10">T10</option>
                        </Select>
                        <Flex
                            direction={{ base: 'row', md: 'row' }}
                            gap={2}
                            justify={{ base: 'flex-start', md: 'flex-start' }}
                            width={{ base: '100%', md: 'auto' }}
                        >
                            {selectedDate && (
                                <Button
                                    size={buttonSize}
                                    variant="ghost"
                                    onClick={() => setSelectedDate('')}
                                    flex={{ base: 1, md: 'auto' }}
                                >
                                    Clear Date
                                </Button>
                            )}
                            {selectedRuleSet && (
                                <Button
                                    size={buttonSize}
                                    variant="ghost"
                                    onClick={() => setSelectedRuleSet('')}
                                    flex={{ base: 1, md: 'auto' }}
                                >
                                    Clear RuleSet
                                </Button>
                            )}
                        </Flex>
                    </Flex>
                </Box>

                {isLoading ? (
                    <LoadingSpinner message="Loading events..." />
                ) : Object.keys(groupedEvents).length === 0 ? (
                    <EmptyState message="No events found for DIM Cricket competitions with the selected date and filters." />
                ) : (
                    Object.entries(groupedEvents)
                        .sort(([dateA], [dateB]) => dateB.localeCompare(dateA)) // Sort in descending order
                        .map(([date, dateEvents]) => (
                            <TradingEventsTable
                                key={date}
                                date={date}
                                events={dateEvents}
                                basePath="/dexch-trading"
                                timeZone={timeZone}
                            />
                        ))
                )}
            </VStack>
        </Box>
    );
};

export const DexchTradingPage: React.FC = () => {
    return (
        <DexchPasswordProtection>
            <DexchTradingContent />
        </DexchPasswordProtection>
    );
};
